import { Image } from 'expo-image';
import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Image as RNImage, StyleSheet, View } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';

import { communities, type CommunityCode } from '~/constants/communities';
import { useSwitchingToCommunity } from '~/libs/navigation/communitySwitchStore';

/**
 * Opaque cover for the community-switch transition (see useCommunitySwitch).
 * Deliberately theme-neutral constant black (bg-black is a literal, not a
 * theme token): it is on screen at the exact moment the community theme
 * flips, so it must not restyle mid-display. Shows the destination
 * community's logo above a spinner. Mounted at the root layout so it survives
 * the navigation reset underneath.
 *
 * Snaps on instantly but FADES out: the destination clears the target from
 * its own mount + a fixed settle delay (see AppLayout), yet native
 * presentation of the fresh tree can still lag — the fade keeps any late
 * frame dimmed until real content is up instead of hard-cutting.
 *
 * className lives on the nested View: css-interop doesn't process className
 * on a Reanimated Animated.View (breaks on web), so the animated layer keeps
 * inline style only.
 */

const OVERLAY_FADE_OUT_MS = 200;
// Logo height matches the mobile header (h-8); width follows the asset's own
// aspect ratio so each community's wordmark keeps its proportions.
const LOGO_HEIGHT = 32;
const LOGO_FALLBACK_WIDTH = 160;

export function CommunitySwitchOverlay() {
  const switchingToCommunity = useSwitchingToCommunity();
  // Retain the target through the fade-out (switchingToCommunity is already
  // null by then), so the logo doesn't vanish before the cover does.
  const [displayCommunity, setDisplayCommunity] = useState<CommunityCode | null>(null);
  const opacity = useSharedValue(0);

  useEffect(() => {
    if (switchingToCommunity) {
      setDisplayCommunity(switchingToCommunity);
      opacity.value = 1;
    } else {
      opacity.value = withTiming(0, { duration: OVERLAY_FADE_OUT_MS }, (finished) => {
        'worklet';
        if (finished) {
          scheduleOnRN(setDisplayCommunity, null);
        }
      });
    }
  }, [switchingToCommunity, opacity]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  const logo = displayCommunity ? communities[displayCommunity]?.logo : undefined;
  const aspectRatio = useMemo(() => {
    if (!logo) return undefined;
    const source = RNImage.resolveAssetSource(logo);
    return source ? source.width / source.height : undefined;
  }, [logo]);

  if (!displayCommunity) {
    return null;
  }

  return (
    <Animated.View
      style={[
        StyleSheet.absoluteFill,
        styles.root,
        // Let touches through as soon as the switch is done (fade-out phase).
        { pointerEvents: switchingToCommunity ? 'auto' : 'none' },
        animatedStyle,
      ]}>
      <View className="flex-1 items-center justify-center gap-8 bg-black">
        {logo ? (
          <Image
            source={logo}
            style={
              aspectRatio
                ? { height: LOGO_HEIGHT, aspectRatio }
                : { height: LOGO_HEIGHT, width: LOGO_FALLBACK_WIDTH }
            }
            contentFit="contain"
            transition={0}
          />
        ) : null}
        <ActivityIndicator size="small" color="#ffffff" />
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  root: {
    zIndex: 50,
  },
});
